import { useEffect, useState } from 'react';
import { type DayWeather, fetchWeather } from '@/api/weather';
import type { Trip } from '@/types/trip';

interface UseWeatherResult {
  weather: Array<DayWeather>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetches daily weather for the trip's date range and location.
 * Falls back to `fallbackLat`/`fallbackLng` if the trip itself has no stored coordinates
 * (e.g. trips created before coordinates were saved, or imported trips).
 */
export const useWeather = (
  trip: Trip,
  fallbackLat?: number | null,
  fallbackLng?: number | null,
): UseWeatherResult => {
  const [weather, setWeather] = useState<Array<DayWeather>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const effectiveLat = trip.lat ?? fallbackLat;
  const effectiveLng = trip.lng ?? fallbackLng;
  const { startDate, endDate } = trip;

  useEffect(() => {
    if (effectiveLat == null || effectiveLng == null) return;

    setIsLoading(true);
    setIsError(false);

    fetchWeather(effectiveLat, effectiveLng, startDate, endDate)
      .then((results) => {
        setWeather(results);
      })
      .catch(() => {
        setIsError(true);
        setWeather([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [effectiveLat, effectiveLng, startDate, endDate]);

  return { weather, isLoading, isError };
};
